/**
 * Utility functions for document validation and handling
 */

/**
 * Determines if a document type can be rendered in the browser
 * @param {string} fileName - The name of the file
 * @returns {boolean} - True if the document can be viewed in browser, false otherwise
 */
export const isBrowserRenderable = (fileName) => {
	if (!fileName) return false;

	const extension = fileName.toLowerCase().split('.').pop();

	// Browser-renderable file types
	const renderableTypes = [
		'pdf',           // PDF documents
		'doc',           // Microsoft Word documents
		'docx',          // Microsoft Word documents
		'jpg',           // JPEG images
		'jpeg',          // JPEG images
		'jpe',           // JPEG images
		'png',           // PNG images
		'gif',           // GIF images
		'webp',          // WebP images
		'svg',           // SVG images
		// TIF and EMF are excluded: browsers don't render either inline
		// (matches isRenderableContentType below, which excludes TIFF
		// explicitly and never lists application/emf as renderable).
		'xps',           // XPS documents
		'txt',           // Plain text files
		'html',          // HTML files
		'htm',           // HTML files
		'xml',           // XML files
		'csv',           // CSV files
		'json',          // JSON files
	];

	return renderableTypes.includes(extension);
};

/**
 * Gets the file extension from a filename
 * @param {string} fileName - The name of the file
 * @returns {string} - The file extension in lowercase
 */
export const getFileExtension = (fileName) => {
	if (!fileName) return '';
	return fileName.toLowerCase().split('.').pop();
};

// Mirrors the extension-to-content-type table in cb_portal_backend's
// utils/encompass_api_helper.py (get_content_type_for_filename) so uploads
// re-derived on the frontend never get mislabeled (e.g. a .txt file
// defaulting to application/pdf when its type is unknown).
const MIME_TYPE_BY_EXTENSION = {
	pdf: 'application/pdf',
	doc: 'application/msword',
	docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
	jpeg: 'image/jpeg',
	jpg: 'image/jpeg',
	jpe: 'image/jpeg',
	png: 'image/png',
	gif: 'image/gif',
	webp: 'image/webp',
	svg: 'image/svg+xml',
	tif: 'image/tiff',
	// Encompass's v3 attachmentUploadUrl API only accepts these exact
	// contentType strings for EMF/XPS (confirmed against its "Create
	// Attachment URL" reference) - the more common image/emf and
	// application/oxps values are rejected with a 400.
	emf: 'application/emf',
	xps: 'application/vnd.ms-xpsdocument',
	html: 'text/html',
	htm: 'text/html',
	txt: 'text/plain',
	xml: 'application/xml',
	csv: 'text/csv',
	json: 'application/json',
};

export const GENERIC_FALLBACK_CONTENT_TYPE = 'application/octet-stream';
export const GENERIC_FALLBACK_FILE_NAME = 'download';

/**
 * Derives a MIME type from a filename's extension.
 * @param {string} fileName - The name of the file
 * @returns {string} - The MIME type, or '' if the extension is unrecognized
 */
export const getMimeTypeFromFilename = (fileName) => {
	const extension = getFileExtension(fileName);
	if (!extension) return '';

	return MIME_TYPE_BY_EXTENSION[extension] || '';
};

/**
 * Determines if a MIME content type is renderable by the browser
 * @param {string} contentType - MIME type string
 * @returns {boolean}
 */
export const isRenderableContentType = (contentType) => {
	const type = (contentType || '').toLowerCase().split(';')[0].trim();
	if (!type) return false;

	// TIFF is commonly not supported inline by browsers.
	if (type === 'image/tiff' || type === 'image/tif') return false;

	return (
		type === 'application/pdf' ||
		type === 'application/msword' ||
		type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
		type.startsWith('image/') ||
		type.startsWith('text/') ||
		type === 'application/json' ||
		type === 'application/xml' ||
		type === 'text/xml' ||
		type === 'text/csv' ||
		type === 'image/svg+xml' ||
		type === 'text/html' ||
		type === 'application/xhtml+xml' ||
		type === 'application/vnd.ms-xpsdocument'
	);
};